import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X } from 'lucide-react';

const mainNavLinks = [
  { name: 'Home', href: ROUTES.HOME },
  { name: 'About', href: ROUTES.ABOUT },
  { name: 'Features', href: ROUTES.FEATURES },
  { name: 'Benefits', href: ROUTES.BENEFITS },
  { name: 'Compare', href: ROUTES.COMPARE },
  { name: 'FAQ', href: ROUTES.FAQ },
  { name: 'Pricing', href: ROUTES.PRICING },
];

const legalLinks = [
  { name: 'Terms of Service', href: ROUTES.TERMS },
  { name: 'Privacy Policy', href: ROUTES.PRIVACY },
  { name: 'SMS Compliance', href: ROUTES.SMS_COMPLIANCE },
  { name: 'Email Compliance', href: ROUTES.EMAIL_COMPLIANCE },
];

export function PublicLayout() {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  function closeMobileMenu() {
    setMobileMenuOpen(false);
  }

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-cacao-dark">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-cacao-dark-tertiary bg-cacao-dark">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            <Link to={ROUTES.HOME} onClick={closeMobileMenu} className="flex items-center gap-2">
              <span className="text-2xl">🐝</span>
              <span className="font-heading font-semibold text-[#F5F5F5]">Traction Bee</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-5">
              <div className="flex items-center gap-4">
                {mainNavLinks.map((link) => {
                  const isActive =
                    link.href === ROUTES.HOME
                      ? location.pathname === link.href
                      : location.pathname === link.href || location.pathname.startsWith(`${link.href}/`);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`text-xs font-medium transition-colors ${
                        isActive ? 'text-white' : 'text-cacao-muted hover:text-white'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {link.name}
                    </Link>
                  );
                })}
              </div>
              {user ? (
                <>
                  <Link
                    to={ROUTES.DASHBOARD}
                    className={`text-xs font-medium transition-colors ${
                      location.pathname.startsWith(ROUTES.DASHBOARD)
                        ? 'text-white'
                        : 'text-cacao-muted hover:text-white'
                    }`}
                    aria-current={location.pathname.startsWith(ROUTES.DASHBOARD) ? 'page' : undefined}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to={ROUTES.SETTINGS}
                    className={`text-xs font-medium transition-colors ${
                      location.pathname.startsWith(ROUTES.SETTINGS)
                        ? 'text-white'
                        : 'text-cacao-muted hover:text-white'
                    }`}
                    aria-current={location.pathname.startsWith(ROUTES.SETTINGS) ? 'page' : undefined}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="rounded-md bg-cacao-dark-secondary px-3 py-1.5 text-xs font-medium text-[#F5F5F5] hover:bg-cacao-dark-tertiary transition-colors"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={ROUTES.LOGIN}
                    className="text-xs font-medium text-cacao-muted hover:text-white transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    to={ROUTES.SIGNUP}
                    className="rounded-md bg-cacao-gold px-3 py-1.5 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile hamburger button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-cacao-muted hover:text-white transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-cacao-gold/50"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-cacao-dark-tertiary bg-cacao-dark-secondary">
            <div className="px-4 py-4 space-y-1">
              {mainNavLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={closeMobileMenu}
                  className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'bg-cacao-dark-tertiary text-white'
                      : 'text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="border-t border-cacao-dark-tertiary my-3" />
              {user ? (
                <>
                  <Link
                    to={ROUTES.DASHBOARD}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to={ROUTES.SETTINGS}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={ROUTES.LOGIN}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-sm font-medium text-cacao-muted hover:bg-cacao-dark-tertiary hover:text-white transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    to={ROUTES.SIGNUP}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-sm font-medium bg-cacao-gold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-14">
        <Outlet />
      </main>

      <footer className="border-t border-cacao-dark-tertiary bg-[#111111]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2">
              <Link to={ROUTES.HOME} className="flex items-center gap-2">
                <span className="text-4xl">🐝</span>
                <span className="font-heading text-xl font-semibold text-[#F5F5F5]">Traction Bee</span>
              </Link>
              <div className="mt-4 text-xs text-cacao-muted">
                <p>26565 Agoura Road, Suite 200</p>
                <p>Calabasas, CA 91302 USA</p>
                <p className="mt-2">
                  <a href="https://tractionbee.com" className="hover:text-white transition-colors">
                    tractionbee.com
                  </a>
                </p>
                <p>
                  <a href="mailto:support@tractionbee.com" className="hover:text-white transition-colors">
                    support@tractionbee.com
                  </a>
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-medium text-cacao-muted uppercase tracking-wide">Legal</h3>
              <ul className="mt-3 space-y-2">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-xs text-cacao-muted hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-medium text-cacao-muted uppercase tracking-wide">Support</h3>
              <ul className="mt-3 space-y-2">
                <li>
                  <Link
                    to={ROUTES.FAQ}
                    className="text-xs text-cacao-muted hover:text-white transition-colors"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTES.SUPPORT}
                    className="text-xs text-cacao-muted hover:text-white transition-colors"
                  >
                    Contact Support
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-xs font-medium text-cacao-muted uppercase tracking-wide">Admin</h3>
              <ul className="mt-3 space-y-2">
                <li>
                  <Link
                    to={ROUTES.STATUS}
                    className="text-xs text-cacao-muted hover:text-white transition-colors"
                  >
                    Project Status
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-cacao-dark-tertiary">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="text-xs text-cacao-text-tertiary">
                &copy; {new Date().getFullYear()} Traction Bee. All rights reserved.
              </p>
              <p className="text-xs text-cacao-text-tertiary">
                Built by{' '}
                <a
                  href="https://www.codexdeus.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cacao-muted hover:text-white transition-colors"
                >
                  CodexDeus
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
